import { useEffect, useState } from "react";
import { IndianRupee, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const gatewayDocs = [
  "1. Login to the merchant's Razorpay dashboard.",
  "2. Open Settings > API Keys and generate test or live key id and secret.",
  "3. Open Webhooks and add your Terminal URL callback when required.",
  "4. If Razorpay asks for a website URL, use your assigned Terminal URL.",
  "5. Paste key id, key secret, and webhook secret here, then activate Razorpay.",
  "6. Test with a small bill before switching to live mode.",
];

const emptyDraft = {
  provider: "razorpay",
  mode: "test",
  key_id: "",
  key_secret: "",
  webhook_secret: "",
};

export default function PaymentGatewaySetup({ context = "Terminal", ownerOnly = false }) {
  const [busy, setBusy] = useState(false);
  const [activeGateway, setActiveGateway] = useState({});
  const [savedGateways, setSavedGateways] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get("/payments/gateway-settings");
      setActiveGateway(res.data || {});
      setSavedGateways(Array.isArray(res.data?.saved_gateways) ? res.data.saved_gateways : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load payment setup");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const save = async () => {
    if (!draft.key_id.trim()) return toast.error("Razorpay Key ID is required");
    if (!draft.key_secret.trim()) return toast.error("Razorpay Key Secret is required");
    setBusy(true);
    try {
      await api.put("/payments/gateway-settings", draft);
      toast.success(`Razorpay activated for ${context} online payments`);
      setDraft((current) => ({ ...current, key_secret: "", webhook_secret: "" }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not activate Razorpay");
    } finally {
      setBusy(false);
    }
  };

  const activate = async (provider) => {
    setBusy(true);
    try {
      await api.post(`/payments/gateway-settings/${provider}/activate`);
      toast.success("Razorpay activated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not activate saved gateway");
    } finally {
      setBusy(false);
    }
  };

  const active = activeGateway?.active;

  return (
    <section className="space-y-5">
      <div className="border border-zinc-800 bg-zinc-900/40 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Payment setup</p>
            <h2 className="mt-1 font-display text-3xl font-black tracking-tight">Merchant Razorpay Activation</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-zinc-500">
              Configure the merchant's own Razorpay account here. Reception and shopkeeper users can then send payment links or open desk collection links without seeing API secrets.
            </p>
          </div>
          <button type="button" onClick={load} disabled={busy} className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500 disabled:opacity-50">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <div className="border border-zinc-800 bg-zinc-950 p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <button type="button" className="border border-orange-500 bg-orange-500/10 px-4 py-4 text-left text-orange-200">
              <p className="font-display text-lg font-semibold">Razorpay</p>
              <p className="mt-1 text-xs text-zinc-500">Use merchant-owned Razorpay keys for this tenant.</p>
            </button>
          </div>

          {!!savedGateways.length && (
            <div className="mt-5 border border-zinc-800 bg-black p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Saved gateways</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {savedGateways.map((gateway) => (
                  <div key={gateway.provider} className={`border p-3 ${gateway.active ? "border-emerald-500/50 bg-emerald-500/10" : "border-zinc-800 bg-zinc-950"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display font-semibold text-zinc-100">{gateway.provider_label || "Razorpay"}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{gateway.mode || "test"} · {gateway.masked_key_id || "saved"}</p>
                      </div>
                      <span className={`border px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${gateway.active ? "border-emerald-500/40 text-emerald-300" : "border-zinc-700 text-zinc-500"}`}>{gateway.active ? "Active" : "Saved"}</span>
                    </div>
                    {!gateway.active && (
                      <button type="button" disabled={busy} onClick={() => activate(gateway.provider)} className="mt-3 w-full border border-orange-500/50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                        Activate
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <GatewayField label="Mode">
              <select value={draft.mode} onChange={(e) => update("mode", e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none focus:border-orange-500">
                <option value="test">Test</option>
                <option value="live">Live</option>
              </select>
            </GatewayField>
            <GatewayInput label="Razorpay Key ID" value={draft.key_id} onChange={(value) => update("key_id", value)} />
            <GatewayInput label="Razorpay Key Secret" value={draft.key_secret} onChange={(value) => update("key_secret", value)} type="password" />
            <GatewayInput label="Webhook secret" value={draft.webhook_secret} onChange={(value) => update("webhook_secret", value)} type="password" />
          </div>

          <button disabled={busy} onClick={save} className="mt-5 inline-flex items-center gap-2 bg-orange-500 px-5 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
            <IndianRupee className="h-4 w-4" /> {busy ? "Saving..." : "Activate Razorpay"}
          </button>
        </div>

        <div className="space-y-4">
          <div className={`border p-5 ${active ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-orange-500/40 bg-orange-500/10 text-orange-300"}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em]">Current status</p>
            <p className="mt-3 font-display text-3xl font-black tracking-tight">{active ? "ACTIVE" : "NOT CONFIGURED"}</p>
            <p className="mt-2 text-sm opacity-80">
              {active ? "Online payment links and desk collection can use this merchant Razorpay account." : "Online payment buttons stay blocked until Razorpay is activated."}
            </p>
          </div>
          <div className="border border-zinc-800 bg-zinc-950 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Active gateway</p>
            {activeGateway?.provider ? (
              <div className="mt-4 space-y-3 text-sm">
                <InfoLine label="Provider" value={activeGateway.provider_label || "Razorpay"} />
                <InfoLine label="Mode" value={activeGateway.mode} />
                <InfoLine label="Key" value={activeGateway.masked_key_id} />
                <InfoLine label="Secret" value={activeGateway.has_key_secret ? "Saved" : "Missing"} />
                <InfoLine label="Webhook" value={activeGateway.has_webhook_secret ? "Saved" : "Optional / missing"} />
              </div>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No gateway activated yet.</p>
            )}
          </div>
          <div className="border border-zinc-800 bg-black p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Manual setup guide</p>
            <div className="mt-4 space-y-3 text-sm text-zinc-400">
              {gatewayDocs.map((line, index) => <p key={index}>{line}</p>)}
            </div>
          </div>
          {ownerOnly && <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">Visible only to admin/tenant admin users.</p>}
        </div>
      </div>
    </section>
  );
}

function GatewayField({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      {children}
    </label>
  );
}

function GatewayInput({ label, value, onChange, type = "text" }) {
  return (
    <GatewayField label={label}>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={label} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm text-zinc-100 outline-none placeholder:text-zinc-700 focus:border-orange-500" />
    </GatewayField>
  );
}

function InfoLine({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b border-zinc-900 pb-2 last:border-b-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className="max-w-[210px] truncate text-right text-zinc-200">{value || "-"}</span>
    </div>
  );
}
